import React, { useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { ThemeProvider } from '@emotion/react';
import { Theme } from 'ui_system/compass';
import { useInactivity } from 'ui_system/hooks/useInactivity';

import { Protected } from 'ui_system/token_auth/routes';

import { Background } from 'ui_system/token_auth/styles_root';

import {
  validateToken,
  validateTokenComplete,
  sessionTimeout,
  dismissSessionTimeout,
  setInitialProps,
} from 'data_system/token_auth/actions';

import {
  getTokenValidating,
  getLoggedIn,
  getSessionTimeout,
} from 'data_system/token_auth/selectors';

import { setContrastColor } from 'ui_system/theme/color_util';

import { loadLocales } from 'services/translation';

import { SessionTimeoutModal } from 'ui_system/token_auth/SessionTimeoutModal';
import { Router } from './routes/Router';
import { Header } from './components/Header';
import { NavBar } from './components/NavBar';

export function AffiliatePortal({ brand }) {
  const dispatch = useDispatch();

  const tokenValidating = useSelector(getTokenValidating);
  const loggedIn = useSelector(getLoggedIn);
  const isSessionTimedOut = useSelector(getSessionTimeout);

  const affiliateBrandColor = Theme.colors.primary;
  const updatedBrand = {
    ...brand,
    buttonBackgroundColor: affiliateBrandColor,
    linkColor: affiliateBrandColor,
    buttonTextColor: setContrastColor(affiliateBrandColor),
  };

  useMemo(() => {
    const locale = new URL(window.location.href).searchParams.get('lang');
    const lang = locale || 'en-US';
    loadLocales(lang);
    return lang;
  }, []);

  useEffect(() => {
    dispatch(setInitialProps({ brand: updatedBrand }));

    // Only try to validate them being logged in if they have a token stored
    if (localStorage.getItem('access-token')) {
      dispatch(validateToken);
    } else {
      dispatch(validateTokenComplete);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useInactivity(loggedIn, () => dispatch(sessionTimeout), 120);

  return (
    <ThemeProvider theme={updatedBrand}>
      <Background white={loggedIn || tokenValidating}>
        <SessionTimeoutModal
          isOpen={isSessionTimedOut}
          onClose={() => dispatch(dismissSessionTimeout)}
        />
        <Protected>
          <NavBar />
        </Protected>
        <Header />
        {!tokenValidating && <Router />}
      </Background>
    </ThemeProvider>
  );
}
